import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useId, useMemo, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { AdminPage, AdminState, EditLayer, Field, LayerButtons, Notice, PrimaryButton, RowMenu, SkeletonList } from '@/admin/AdminShell'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { ConfirmModal, StateView, type MenuAction } from './record'
import {
  createCategory,
  deleteCategory,
  flattenTree,
  subtreeOf,
  updateCategory,
  useCategories,
  type ProductCategory,
} from './productApi'

/**
 * Дерево категорий товаров (V60): завести, переименовать, перенести, удалить.
 *
 * Живёт в «Продажах» рядом с номенклатурой, а не в администрировании, и правится правами
 * на товары (`sales.product`): категории — структура каталога, их ведёт тот, кто заводит
 * товары. Отдать их `system.settings` значило бы, что руководитель продаж заводит товар,
 * но не может завести для него ветку.
 *
 * Удалить можно только пустую категорию. Непустая показывает пункт «Удалить» недоступным
 * **с причиной** («3 товара, 1 подкатегория»), а не прячет его: спрятанный пункт выглядит
 * как отсутствующая функция, и человек идёт её искать.
 */
type Sheet =
  | { mode: 'create'; parentId: string | null }
  | { mode: 'edit'; category: ProductCategory }

export function ProductCategoriesPage() {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const categories = useCategories()
  const [sheet, setSheet] = useState<Sheet | null>(null)
  const [removing, setRemoving] = useState<ProductCategory | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const mayCreate = can(user, 'sales.product', 'create')
  const mayUpdate = can(user, 'sales.product', 'update')
  const mayDelete = can(user, 'sales.product', 'delete')
  const all = useMemo(() => categories.data ?? [], [categories.data])
  const tree = useMemo(() => flattenTree(all), [all])
  const byId = useMemo(() => new Map(all.map((category) => [category.id, category])), [all])

  // A collapsed ancestor hides the whole branch, not only the direct children.
  const hidden = (category: ProductCategory): boolean => {
    let parent = category.parentId != null ? byId.get(category.parentId) : undefined
    for (let depth = 0; parent && depth < 64; depth++) {
      if (collapsed.has(parent.id)) return true
      parent = parent.parentId != null ? byId.get(parent.parentId) : undefined
    }
    return false
  }

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['product-categories'] })
    void queryClient.invalidateQueries({ queryKey: ['list', 'sales.product'] })
  }

  const remove = useMutation({
    mutationFn: (category: ProductCategory) => deleteCategory(category.id),
    onSuccess: () => {
      setRemoving(null)
      setNotice(null)
      refresh()
    },
    onError: (error) => {
      setRemoving(null)
      setNotice(error instanceof ApiError ? error.message : t('app.error'))
    },
  })

  const menuOf = (category: ProductCategory): MenuAction[] => {
    const items: MenuAction[] = []
    if (mayCreate) {
      items.push({ id: 'child', label: t('category.addChild'), icon: 'plus', onSelect: () => setSheet({ mode: 'create', parentId: category.id }) })
    }
    if (mayUpdate) {
      items.push({ id: 'edit', label: t('category.edit'), icon: 'edit', onSelect: () => setSheet({ mode: 'edit', category }) })
    }
    if (mayDelete) {
      const busy = category.products > 0 || category.children > 0
      items.push({
        id: 'delete',
        label: t('category.delete'),
        icon: 'trash',
        danger: true,
        onSelect: () => setRemoving(category),
        disabledReason: busy ? t('category.notEmpty', { products: category.products, children: category.children }) : undefined,
      })
    }
    return items
  }

  return (
    <AdminPage
      titleKey="nav.productCategories"
      sectionKey="nav.section.sales"
      count={categories.data ? all.length : undefined}
      leadKey="category.lead"
      readOnly={!mayCreate && !mayUpdate && !mayDelete}
      roKey="category.ro"
      primary={mayCreate ? (
        <PrimaryButton label={t('category.addRoot')} onClick={() => setSheet({ mode: 'create', parentId: null })} />
      ) : undefined}
    >
      <Notice text={notice} />
      <AdminState isLoading={categories.isLoading} error={categories.error} onRetry={() => void categories.refetch()}
                  skeleton={<SkeletonList />}>
        {tree.length === 0 ? (
          <StateView boxed kind="empty" title={t('category.empty')} description={mayCreate ? t('category.emptyText') : undefined} />
        ) : (
          <ul className="ct-tree" role="tree" aria-label={t('nav.productCategories')}>
            {tree.filter(({ category }) => !hidden(category)).map(({ category, depth }) => {
              const open = !collapsed.has(category.id)
              const menu = menuOf(category)
              return (
                <li key={category.id} role="treeitem" aria-level={depth + 1}
                    aria-expanded={category.children > 0 ? open : undefined}
                    className="ct-node" style={{ paddingLeft: 8 + depth * 22 }}>
                  {category.children > 0 ? (
                    <button type="button" className="btn btn-ghost btn-sm btn-icon"
                            aria-label={t(open ? 'category.collapse' : 'category.expand', { name: category.name })}
                            onClick={() => setCollapsed((current) => {
                              const next = new Set(current)
                              if (next.has(category.id)) next.delete(category.id)
                              else next.add(category.id)
                              return next
                            })}>
                      <Icon name="chevron" size="sm" className={open ? 'ct-chev is-open' : 'ct-chev'} />
                    </button>
                  ) : (
                    <span className="ct-spacer" aria-hidden="true" />
                  )}
                  <span className="ct-name">{category.name}</span>
                  <span className="t-sm muted">
                    {t('category.counts', { products: category.products, children: category.children })}
                  </span>
                  <span className="spacer" />
                  <span className="mono t-sm muted">{category.code}</span>
                  {menu.length > 0 && <RowMenu label={t('category.menu', { name: category.name })} items={menu} />}
                </li>
              )
            })}
          </ul>
        )}
      </AdminState>

      {sheet && (
        <CategorySheet
          sheet={sheet}
          all={all}
          onClose={() => setSheet(null)}
          onSaved={() => {
            setSheet(null)
            refresh()
          }}
        />
      )}

      <ConfirmModal
        open={removing != null}
        title={t('category.deleteTitle', { name: removing?.name ?? '' })}
        confirmLabel={t('category.delete')}
        pending={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing)}
      >
        <p>{t('category.deleteText')}</p>
      </ConfirmModal>
    </AdminPage>
  )
}

function CategorySheet({ sheet, all, onClose, onSaved }: {
  sheet: Sheet
  all: ProductCategory[]
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useApp()
  const formId = useId()
  const editing = sheet.mode === 'edit' ? sheet.category : null
  const [name, setName] = useState(editing?.name ?? '')
  const [parentId, setParentId] = useState<string>(editing ? editing.parentId ?? '' : sheet.mode === 'create' ? sheet.parentId ?? '' : '')
  const [notice, setNotice] = useState<string | null>(null)
  // A category cannot move into its own branch: those options are not offered at all.
  const forbidden = useMemo(() => (editing ? subtreeOf(all, editing.id) : new Set<string>()), [all, editing])
  const tree = useMemo(() => flattenTree(all).filter(({ category }) => !forbidden.has(category.id)), [all, forbidden])

  const save = useMutation({
    mutationFn: () => {
      if (editing) {
        const moved = (editing.parentId ?? '') !== parentId
        return updateCategory(editing.id, {
          name: name.trim(),
          version: editing.version,
          ...(moved ? (parentId === '' ? { toRoot: true } : { parentId }) : {}),
        })
      }
      return createCategory({ name: name.trim(), parentId: parentId === '' ? null : parentId })
    },
    onSuccess: onSaved,
    onError: (error) => setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  return (
    <EditLayer
      kind="sheet"
      overline={t('nav.productCategories')}
      title={editing ? t('category.editTitle') : t('category.createTitle')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('admin.save')} pending={save.isPending}
                            disabled={name.trim() === ''} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 12 }}
            onSubmit={(event) => {
              event.preventDefault()
              setNotice(null)
              save.mutate()
            }}>
        <Notice text={notice} />
        <Field label={t('category.name')} htmlFor={`${formId}-name`} required>
          <input id={`${formId}-name`} className="input" value={name} maxLength={255}
                 onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label={t('category.parent')} htmlFor={`${formId}-parent`} help={editing ? t('category.parentHelp') : undefined}>
          <select id={`${formId}-parent`} className="select" value={parentId}
                  onChange={(event) => setParentId(event.target.value)}>
            <option value="">{t('category.root')}</option>
            {tree.map(({ category, depth }) => (
              <option key={category.id} value={category.id}>{`${' '.repeat(depth * 3)}${category.name}`}</option>
            ))}
          </select>
        </Field>
      </form>
    </EditLayer>
  )
}
